/**
 * @fileoverview Authentication Zod schemas with runtime validation
 */

import { z } from 'zod';

/** Password requirements */
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;

/** 
 * Common disposable/temporary email domains to block
 * This is a minimal list - in production, consider using a maintained blocklist service
 */
const DISPOSABLE_EMAIL_DOMAINS = [
  'tempmail.com', 'temp-mail.org', 'guerrillamail.com', 'guerrillamail.org',
  'mailinator.com', 'mailinator.net', '10minutemail.com', '10minmail.com',
  'throwaway.email', 'throwawaymail.com', 'fakeinbox.com', 'sharklasers.com',
  'spam4.me', 'yopmail.com', 'yopmail.fr', 'trashmail.com', 'trashmail.net',
  'getnada.com', 'tempail.com', 'mohmal.com', 'dispostable.com', 'mailnesia.com',
  'maildrop.cc', 'mintemail.com', 'mt2015.com', 'emailondeck.com', 'emailfake.com',
  'crazymailing.com', 'tempinbox.com', 'tempr.email', 'discard.email', 'discardmail.com',
];

/**
 * Enhanced email validation that checks:
 * 1. Valid email format (Zod built-in)
 * 2. Max length per RFC 5321 (254 chars)
 * 3. Local part max 64 chars
 * 4. Domain has at least one dot
 * 5. Not a disposable email domain
 * 6. No consecutive dots
 * 7. Doesn't start/end with dot
 */
const emailSchema = z.string()
  .email('Ugyldig e-postadresse')
  .max(254, 'E-postadresse er for lang')
  .refine((email) => {
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) return false;
    if (localPart.length > 64) return false;
    if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
    if (localPart.includes('..')) return false;
    return true;
  }, 'Ugyldig e-postformat')
  .refine((email) => {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return false;
    return !DISPOSABLE_EMAIL_DOMAINS.includes(domain);
  }, 'Midlertidige e-postadresser er ikke tillatt')
  .transform((email) => email.toLowerCase().trim());

/** Self-register request schema (for open registration) */
export const selfRegisterRequestSchema = z.object({
  email: emailSchema,
  password: z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Passord må være minst ${PASSWORD_MIN_LENGTH} tegn`)
    .regex(PASSWORD_REGEX, 'Passord må inneholde minst én liten bokstav, én stor bokstav og ett tall'),
  displayName: z
    .string()
    .min(2, 'Visningsnavn må være minst 2 tegn')
    .max(100, 'Visningsnavn kan ikke være mer enn 100 tegn'),
  language: z.enum(['nb', 'en']).optional().default('nb'),
});

export type SelfRegisterRequestInput = z.infer<typeof selfRegisterRequestSchema>;

/** Register with invitation token schema */
export const registerRequestSchema = z.object({
  token: z.string().min(1, 'Invitasjonstoken er påkrevd'),
  password: z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Passord må være minst ${PASSWORD_MIN_LENGTH} tegn`)
    .regex(PASSWORD_REGEX, 'Passord må inneholde minst én liten bokstav, én stor bokstav og ett tall'),
  displayName: z
    .string()
    .min(2, 'Visningsnavn må være minst 2 tegn')
    .max(100, 'Visningsnavn kan ikke være mer enn 100 tegn'),
  language: z.enum(['nb', 'en']).optional().default('nb'),
});

export type RegisterRequestInput = z.infer<typeof registerRequestSchema>;

/** Login request schema - uses simpler email validation since user already exists */
export const loginRequestSchema = z.object({
  email: z.string().email('Ugyldig e-postadresse').transform((e) => e.toLowerCase().trim()),
  password: z.string().min(1, 'Passord er påkrevd'),
});

export type LoginRequestInput = z.infer<typeof loginRequestSchema>;

/** Forgot password schema */
export const forgotPasswordRequestSchema = z.object({
  email: z.string().email('Ugyldig e-postadresse').transform((e) => e.toLowerCase().trim()),
});

export type ForgotPasswordRequestInput = z.infer<typeof forgotPasswordRequestSchema>;

/** Verify email schema */
export const verifyEmailRequestSchema = z.object({
  token: z.string().min(1, 'Verifiseringstoken er påkrevd'),
});

export type VerifyEmailRequestInput = z.infer<typeof verifyEmailRequestSchema>;

/** Resend verification email schema */
export const resendVerificationRequestSchema = z.object({
  email: z.string().email('Ugyldig e-postadresse').transform((e) => e.toLowerCase().trim()),
});

export type ResendVerificationRequestInput = z.infer<typeof resendVerificationRequestSchema>;

/** Reset password schema */
export const resetPasswordRequestSchema = z.object({
  token: z.string().min(1, 'Token er påkrevd'),
  password: z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Passord må være minst ${PASSWORD_MIN_LENGTH} tegn`)
    .regex(PASSWORD_REGEX, 'Passord må inneholde minst én liten bokstav, én stor bokstav og ett tall'),
});

export type ResetPasswordRequestInput = z.infer<typeof resetPasswordRequestSchema>;

/** Refresh token schema */
export const refreshTokenRequestSchema = z.object({
  refreshToken: z.string().min(1, 'Refresh token er påkrevd'),
});

export type RefreshTokenRequestInput = z.infer<typeof refreshTokenRequestSchema>;

/** Change password schema (for logged-in users) */
export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1, 'Nåværende passord er påkrevd'),
  newPassword: z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Nytt passord må være minst ${PASSWORD_MIN_LENGTH} tegn`)
    .regex(PASSWORD_REGEX, 'Passord må inneholde minst én liten bokstav, én stor bokstav og ett tall'),
});

export type ChangePasswordRequestInput = z.infer<typeof changePasswordRequestSchema>;
